<template>
  <addon-layout
    title="Ajouter un parcours"
    subtitle="Choisissez le parcours TUT’TOP à joindre à cette publication."
    :loading="loading"
    :error="error"
  >
    <section
      v-if="needsGoogleAuth"
      class="link-card"
    >
      <h2>Se connecter avec Google</h2>
      <p>Confirmez le compte Google actuellement ouvert dans Classroom.</p>
      <google-sign-in-button
        @click="connectGoogle"
      />
    </section>
    <section
      v-else-if="!tutorLinked"
      class="link-card"
    >
      <template v-if="pendingLinkCode">
        <h2>Confirmer le compte professeur</h2>
        <p>
          {{ pendingAccount
            ? `${pendingAccount.name || 'Professeur'} — ${pendingAccount.email || ''}`
            : 'Compte professeur TUT’TOP détecté' }}
        </p>
        <button
          type="button"
          class="primary"
          @click="confirmAccountLink"
        >
          Confirmer la liaison
        </button>
        <button
          type="button"
          class="secondary"
          @click="cancelAccountLink"
        >
          Utiliser un autre compte
        </button>
      </template>
      <template v-else>
        <h2>Relier le compte professeur</h2>
        <p>
          Vos parcours sont créés dans TUT’TOP. Connectez-vous pour les afficher ici,
          ou créez votre compte professeur si vous n’en avez pas encore.
        </p>
        <p
          v-if="linkStatus"
          class="link-status"
        >
          {{ linkStatus }}
        </p>
        <button
          type="button"
          class="primary"
          :disabled="!!linkStatus"
          @click="openTutorLogin"
        >
          Se connecter à TUT’TOP
        </button>
        <button
          type="button"
          class="primary"
          :disabled="!!linkStatus"
          @click="createTutorAccount"
        >
          Créer mon compte TUT’TOP
        </button>
        <button
          type="button"
          class="secondary"
          :disabled="!!linkStatus"
          @click="linkCurrentAccount"
        >
          J’ai terminé la connexion
        </button>
      </template>
    </section>

    <template v-else>
      <div
        v-if="workspaces.length"
        class="workspace-grid"
      >
        <button
          v-for="workspace in workspaces"
          :key="workspace.id"
          type="button"
          class="workspace"
          :class="{ selected: selectedId === workspace.id }"
          @click="selectedId = workspace.id"
        >
          <strong>{{ workspace.name }}</strong>
          <span>{{ workspace.classroomName }}</span>
          <small>Version {{ workspace.version || 1 }}</small>
        </button>
      </div>
      <section
        v-else
        class="empty"
      >
        <h2>Aucun parcours publié</h2>
        <p>Créez un parcours dans TUT’TOP, publiez-le, puis il apparaîtra ici.</p>
        <p
          v-if="linkStatus"
          class="link-status"
        >
          {{ linkStatus }}
        </p>
        <button
          type="button"
          class="primary"
          :disabled="!!linkStatus"
          @click="createFirstWorkspace"
        >
          Créer mon premier parcours
        </button>
        <button
          type="button"
          class="secondary"
          :disabled="!!linkStatus"
          @click="loadWorkspaces"
        >
          Actualiser
        </button>
      </section>
      <footer class="actions">
        <button
          type="button"
          class="secondary"
          @click="closeIframe"
        >
          Annuler
        </button>
        <button
          type="button"
          class="primary"
          :disabled="!selectedId || attaching"
          @click="attach"
        >
          {{ attaching ? 'Ajout…' : 'Ajouter à Classroom' }}
        </button>
      </footer>
    </template>
  </addon-layout>
</template>

<script>
import {
  authorize,
  client,
  hasAddonSession,
  launchContext,
} from '@/services/googleClassroomAddon'
import { isTrustedAddonMessage } from '@/services/googleClassroomLaunchContext'
import AddonLayout from './AddonLayout.vue'
import GoogleSignInButton from './GoogleSignInButton.vue'

// The bridge answers immediately, while a full sign-in or sign-up flow
// (form, e-mail verification, login) needs a much longer window.
const BRIDGE_TIMEOUT_MS = 30000
const AUTH_FLOW_TIMEOUT_MS = 15 * 60 * 1000

export default {
  name: 'GoogleClassroomAddonDiscovery',
  components: { AddonLayout, GoogleSignInButton },
  data() {
    return {
      loading: true,
      attaching: false,
      error: '',
      needsGoogleAuth: !hasAddonSession(),
      tutorLinked: false,
      workspaces: [],
      selectedId: '',
      pendingLinkCode: '',
      pendingAccount: null,
      linkStatus: '',
    }
  },
  async created() {
    if (hasAddonSession()) await this.initialize()
    else this.loading = false
  },
  methods: {
    async initialize() {
      this.loading = true
      this.error = ''
      try {
        const response = await client.post('/context', launchContext(this.$route))
        if (response.data.role !== 'teacher') throw new Error('Ce panneau est réservé au professeur.')
        this.needsGoogleAuth = false
        this.tutorLinked = response.data.tutorLinked
        if (this.tutorLinked) await this.loadWorkspaces()
      } catch (error) {
        this.error = error.response?.data?.error || error.message || 'Impossible de charger l’add-on.'
        if (error.response && error.response.status === 401) this.needsGoogleAuth = true
      } finally {
        this.loading = false
      }
    },
    async connectGoogle() {
      this.loading = true
      this.error = ''
      try {
        await authorize(this.$route)
        this.needsGoogleAuth = false
        await this.initialize()
      } catch (error) {
        this.error = error.response?.data?.error || error.message
        this.loading = false
      }
    },
    // Resolves once the account-link bridge posts a single-use code back,
    // whatever path the popup took to reach it (sign-in or sign-up).
    waitForAccountLink(popup, timeoutMs, timeoutMessage) {
      return new Promise((resolve, reject) => {
        let timer
        const onMessage = event => {
          const trustedSuccess = isTrustedAddonMessage(event, {
            origin: window.location.origin,
            type: 'TUTTOP_ACCOUNT_LINK',
            source: popup,
          })
          const trustedError = isTrustedAddonMessage(event, {
            origin: window.location.origin,
            type: 'TUTTOP_ACCOUNT_LINK_ERROR',
            source: popup,
          })
          if (!trustedSuccess && !trustedError) return
          window.clearTimeout(timer)
          window.removeEventListener('message', onMessage)
          if (trustedError) reject(new Error(event.data.error))
          else resolve({ linkCode: event.data.linkCode, account: event.data.account || null })
        }
        window.addEventListener('message', onMessage)
        timer = window.setTimeout(() => {
          window.removeEventListener('message', onMessage)
          reject(new Error(timeoutMessage))
        }, timeoutMs)
      })
    },
    async runAccountFlow({
      path, timeoutMs, status, timeoutMessage,
    }) {
      this.error = ''
      const popup = window.open(path, 'tuttop-account-bridge', 'width=560,height=760')
      if (!popup) {
        this.error = 'Autorisez les pop-up pour relier votre compte professeur.'
        return
      }
      this.linkStatus = status
      try {
        const result = await this.waitForAccountLink(popup, timeoutMs, timeoutMessage)
        this.pendingLinkCode = result.linkCode
        this.pendingAccount = result.account
      } catch (error) {
        this.error = error.response?.data?.error || error.message
      } finally {
        this.linkStatus = ''
      }
    },
    openTutorLogin() {
      return this.runAccountFlow({
        path: '/login/tutor?addonLink=1',
        timeoutMs: AUTH_FLOW_TIMEOUT_MS,
        status: 'Terminez la connexion dans la fenêtre TUT’TOP, le retour ici est automatique.',
        timeoutMessage: 'Connexion non terminée. Réessayez ou cliquez sur « J’ai terminé la connexion ».',
      })
    },
    createTutorAccount() {
      return this.runAccountFlow({
        path: '/register/tutor?addonLink=1',
        timeoutMs: AUTH_FLOW_TIMEOUT_MS,
        status: 'Terminez votre inscription dans la fenêtre TUT’TOP, le retour ici est automatique.',
        timeoutMessage: 'Inscription non terminée. Réessayez ou cliquez sur « J’ai terminé la connexion ».',
      })
    },
    linkCurrentAccount() {
      return this.runAccountFlow({
        path: '/classroom-addon/link-account',
        timeoutMs: BRIDGE_TIMEOUT_MS,
        status: 'Vérification de votre session TUT’TOP…',
        timeoutMessage: 'Aucune session professeur TUT’TOP trouvée.',
      })
    },
    async confirmAccountLink() {
      this.error = ''
      try {
        const response = await client.post('/link-account', { linkCode: this.pendingLinkCode })
        if (response.data.role !== 'tutor') throw new Error('Connectez un compte professeur TUT’TOP.')
        this.pendingLinkCode = ''
        this.pendingAccount = null
        this.tutorLinked = true
        await this.loadWorkspaces()
      } catch (error) {
        this.error = error.response?.data?.error || error.message
      }
    },
    cancelAccountLink() {
      this.pendingLinkCode = ''
      this.pendingAccount = null
    },
    async loadWorkspaces() {
      this.error = ''
      try {
        const response = await client.get('/workspaces')
        this.workspaces = response.data.workspaces || []
        if (!this.selectedId && this.workspaces.length === 1) {
          this.selectedId = this.workspaces[0].id
        }
      } catch (error) {
        this.error = error.response?.data?.error || error.message
      }
    },
    async createFirstWorkspace() {
      this.error = ''
      this.linkStatus = 'Création du parcours…'
      try {
        const response = await client.post('/workspaces/bootstrap', launchContext(this.$route))
        const editorUrl = response.data && response.data.editorUrl ? String(response.data.editorUrl) : ''
        const workspaceId = response.data && response.data.workspace && response.data.workspace.id
          ? String(response.data.workspace.id)
          : ''
        if (!editorUrl) throw new Error('Lien d’édition manquant.')

        const popup = window.open(editorUrl, 'tuttop-workspace-editor', 'width=1200,height=820')
        if (!popup) throw new Error('Autorisez les pop-up pour créer le parcours.')

        this.linkStatus = 'Dans la fenêtre TUT’TOP, publiez le parcours. Le retour ici est automatique.'

        const onMessage = event => {
          const trusted = isTrustedAddonMessage(event, {
            origin: window.location.origin,
            type: 'TUTTOP_WORKSPACE_PUBLISHED',
            source: popup,
          })
          if (!trusted) return
          window.removeEventListener('message', onMessage)
          this.loadWorkspaces().then(() => {
            if (workspaceId) this.selectedId = workspaceId
          })
          this.linkStatus = ''
        }
        window.addEventListener('message', onMessage)

        // Fallback: if the popup is closed without emitting the message, refresh anyway.
        const closeWatcher = window.setInterval(() => {
          if (!popup || popup.closed) {
            window.clearInterval(closeWatcher)
            window.removeEventListener('message', onMessage)
            this.loadWorkspaces()
            this.linkStatus = ''
          }
        }, 1200)
      } catch (error) {
        this.error = error.response?.data?.error || error.message
        this.linkStatus = ''
      }
    },
    async attach() {
      this.attaching = true
      this.error = ''
      try {
        await client.post('/attachments', {
          ...launchContext(this.$route),
          workspaceId: this.selectedId,
        })
        this.closeIframe()
      } catch (error) {
        this.error = error.response?.data?.error || error.message || 'L’ajout a échoué.'
      } finally {
        this.attaching = false
      }
    },
    closeIframe() {
      window.parent.postMessage(
        { type: 'Classroom', action: 'closeIframe' },
        'https://classroom.google.com',
      )
    },
  },
}
</script>

<style scoped>
.link-card,
.empty {
  padding: 24px;
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  background: white;
  text-align: center;
}
.workspace-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 12px;
}
.workspace {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 110px;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: white;
  color: #172033;
  text-align: left;
}
.workspace.selected {
  border-color: #635bff;
  background: #f3f1ff;
}
.workspace span,
.workspace small {
  color: #64748b;
}
.link-status {
  margin: 10px 0 0;
  color: #635bff;
  font-weight: 600;
  font-size: 0.9rem;
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}
button.primary,
button.secondary {
  margin: 6px;
  padding: 10px 16px;
  border-radius: 9px;
  border: 0;
  font-weight: 700;
}
button.primary { background: #635bff; color: white; }
button.secondary { background: #e9eef6; color: #334155; }
button:disabled { opacity: .55; }
</style>
